// src/components/ProcessChangesModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    LinearProgress,
    Alert,
    Tooltip,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormControlLabel,
    Switch,
    TableContainer,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import axios from "axios";
import LinkToFacilityResponseModal from "./LinkToFacilityResponseModal";
import { useAuth } from "../components/TokenHandler";

const DEBUG_PAYLOADS = process.env.NODE_ENV !== "production";

// Date-only aware formatter: preserves "YYYY-MM-DD" as-is; strips time from ISO.
const fmt = (d) => {
    if (!d) return null;
    if (typeof d === "string") {
        const m = d.match(/^(\d{4}-\d{2}-\d{2})(?:[T ].*)?$/);
        if (m) return m[1];
    }
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    return dt.toISOString().slice(0, 10);
};



const sanitizeStatus = (s) => {
    const v = String(s || "").toLowerCase().trim();
    if (v === "all" || v === "pending" || v === "denied") return v;
    if (v.includes("pending")) return "pending";
    if (v.includes("denied")) return "denied";
    return "all";
};

const makeKey = (rId, changeId) => `${rId}|${changeId || "base"}`;
const statusLabel = (s) =>
    s === "pending" ? "Pending" : s ? s[0].toUpperCase() + s.slice(1) : "";
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

const COLS_BASE = {
    sel: 44,
    facility: 360,
    service: 120,
    start: 110,
    stop: 110,
    onHold: 120,
    newStart: 120,
    newStop: 120,
    changeDate: 130,
    status: 110,
};

// Safe uuid generator
const makeUUID = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    // tiny fallback
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

export default function ProcessChangesModal({ open, onClose }) {

    const {
        keycloak: kc,
        authenticated,
        infoMessage,
        setInfoMessage,
        givenName,
        email,
        roles,
    } = useAuth();

    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [ok, setOk] = useState("");
    const [apiData, setApiData] = useState(null);
    const [selected, setSelected] = useState(() => new Set());

    const [status, setStatus] = useState("all");
    const [showAll, setShowAll] = useState(false);
    const [facilityQuery, setFacilityQuery] = useState("");

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const controlsRef = useRef(null);
    const debounceRef = useRef(null);

    const [nameLimit] = useState(54);
    const [facilityColPx] = useState(COLS_BASE.facility);

    // response modal state (non-200s/conflicts OR deny+dates)
    const [linkResp, setLinkResp] = useState(null);
    const [showLinkRespModal, setShowLinkRespModal] = useState(false);
    const [linkRespMode, setLinkRespMode] = useState("accept"); // "accept" | "deny"

    const total = apiData?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    // ---------- Fetcher ----------
    const fetchData = async (overrides = {}) => {
        const {
            status: s = status,
            showAll: sa = showAll,
            facility_name: fn = facilityQuery,
            page: pg = page,
            pageSize: psz = pageSize,
        } = overrides;

        const apiStatus = sanitizeStatus(s);

        setErr("");
        setOk("");
        setLoading(true);
        try {
            const body = {
                page: pg,
                pageSize: psz,
                facility_name: fn || "",
                service_name: "",
                itemid: "",
                status: apiStatus,
                showAll: !!sa,
            };
            if (DEBUG_PAYLOADS) {
                console.groupCollapsed("[ProcessChangesModal] view_changes2 request");
                console.log(body);
                console.groupEnd();
            }

            const { data } = await axios.post(
                `${process.env.REACT_APP_API_URL}/facility/view_changes2`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                    },
                }
            );
            setApiData(data);
            setSelected(new Set()); // reset selection on refetch
            if (typeof data?.page === "number") setPage(Number(data.page) || 1);
            if (typeof data?.pageSize === "number") setPageSize(Number(data.pageSize) || psz);
        } catch (e) {
            setErr(e?.response?.data?.error || e?.message || "Failed to load changes.");
        } finally {
            setLoading(false);
        }
    };

    // initial load
    useEffect(() => {
        if (open) {
            setStatus("all");
            setShowAll(false);
            setFacilityQuery("");
            setPage(1);
            setPageSize(50);
            fetchData({
                status: "all",
                showAll: false,
                facility_name: "",
                page: 1,
                pageSize: 50,
            });
        } else {
            setApiData(null);
            setErr("");
            setOk("");
            setSelected(new Set());
            setLinkResp(null);
            setShowLinkRespModal(false);
            setLinkRespMode("accept");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // ---------- Flatten rows ----------
    const flatRows = useMemo(() => {
        if (!apiData?.rows?.length) return [];
        const out = [];
        for (const r of apiData.rows) {
            const changes = Array.isArray(r.changes) ? r.changes : [];
            if (changes.length > 0) {
                for (const ch of changes) {
                    out.push({
                        key: makeKey(r.id, ch.change_id),
                        selectable: true,
                        parentId: r.id,
                        change_id: ch.change_id,
                        linked_to: r.linked_to,
                        facility_name: r.facility_name,
                        service_name: r.service_name,
                        itemid: r.itemid,
                        start_date: r.start_date,
                        end_date: r.end_date,
                        new_start_date: ch.start_date || null,
                        new_end_date: ch.end_date || null,
                        on_hold_date: ch.on_hold_date ?? r.on_hold_date ?? null,
                        change_date: ch.change_date,
                        accepted: ch.accepted,
                        changes: [ch],
                    });
                }
            } else {
                out.push({
                    key: makeKey(r.id, null),
                    selectable: true,
                    parentId: r.id,
                    change_id: null,
                    linked_to: r.linked_to,
                    facility_name: r.facility_name,
                    service_name: r.service_name,
                    itemid: r.itemid,
                    start_date: r.start_date,
                    end_date: r.end_date,
                    new_start_date: null,
                    new_end_date: null,
                    on_hold_date: r.on_hold_date ?? null,
                    change_date: r.latest_change_date || null,
                    accepted: "",
                });
            }
        }
        return out;
    }, [apiData]);

    const selectableKeys = flatRows.filter((r) => r.selectable).map((r) => r.key);
    const selectedCount = selected.size;

    // ---------- Selection ----------
    const toggleOne = (key) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };
    const toggleAll = () => {
        setSelected((prev) =>
            prev.size === selectableKeys.length ? new Set() : new Set(selectableKeys)
        );
    };

    // ---------- Selected parents ----------
    const buildSelectedObjects = () => {
        if (!apiData?.rows?.length) return [];
        const out = [];
        const selectedSet = selected;
        for (const r of apiData.rows) {
            const changes = Array.isArray(r.changes) ? r.changes : [];
            const picked = [];
            for (const ch of changes) {
                const key = makeKey(r.id, ch.change_id);
                if (selectedSet.has(key)) picked.push(ch);
            }
            if (picked.length > 0) {
                out.push({ ...r, changes: picked });
            } else {
                const baseKey = makeKey(r.id, null);
                if (selectedSet.has(baseKey)) {
                    out.push({ ...r, changes: [] });
                }
            }
        }
        return out;
    };

    const getPickedChange = (row) =>
        Array.isArray(row.changes) && row.changes.length > 0 ? row.changes[0] : null;

    // ---------- Build payloads for direct POST paths ----------
    const buildPayloadAccept = (selectedParents) =>
        selectedParents.map((r) => {
            const picked = getPickedChange(r);
            const payload = { ...r };
            if (picked) {
                payload.changes = [
                    {
                        ...picked,
                        accepted: "accepted",
                        change_date: new Date().toISOString(),
                    },
                ];
            } else {
                delete payload.changes;
            }
            return payload;
        });

    const buildPayloadDenySimple = (selectedParents) =>
        selectedParents
            .filter((r) => Array.isArray(r.changes) && r.changes.length > 0)
            .map((r) => {
                const picked = getPickedChange(r) || r.changes[0];
                const { linked_to, ...rest } = { ...r };
                rest.changes = [
                    {
                        ...picked,
                        accepted: "denied",
                        change_date: new Date().toISOString(),
                    },
                ];
                return rest;
            });

    // ---------- NEW: Build payload for the RESOLVER (deny+dates) ----------
    // This does NOT post. It prepares items so the resolver modal can collect top-level dates.
    const buildModalPayloadForDenyWithDates = (selectedParents) => {
        const nowISO = new Date().toISOString();

        return selectedParents.map((r) => {
            // clone row
            const base = {
                ...r,
                // user will populate these in the next modal
                start_date: "",
                end_date: "",
                on_hold_date: "",
            };

            // ensure "changes" exists with a single entry representing the CURRENT record
            // (this is the "denied" change we are logging)
            const existingChange =
                (Array.isArray(r.changes) && r.changes[0]) || null;

            const changeObj = existingChange
                ? {
                    ...existingChange,
                    accepted: "denied",
                    change_date: nowISO,
                }
                : {
                    itemid: r.itemid,
                    service_name: r.service_name,
                    change_id: makeUUID(),
                    start_date: fmt(r.start_date),
                    end_date: r.end_date ? fmt(r.end_date) : null,
                    on_hold_date: r.on_hold_date ? fmt(r.on_hold_date) : null,
                    change_date: nowISO,
                    accepted: "denied",
                };

            base.changes = [changeObj];

            return base;
        });
    };

    // ---------- POST helper ----------
    const sendToLinkToFacility = async (payloadArray, successMsg) => {
        setErr("");
        setOk("");

        if (DEBUG_PAYLOADS) {
            try {
                console.groupCollapsed("[ProcessChangesModal] linkToFacility payload");
                console.log("Records:", payloadArray.length);
                const preview = payloadArray.map((p) => ({
                    id: p.id,
                    itemid: p.itemid,
                    service_name: p.service_name,
                    changes_count: Array.isArray(p.changes) ? p.changes.length : 0,
                    linked_to: Object.prototype.hasOwnProperty.call(p, "linked_to")
                        ? (p.linked_to === null ? "null" : "present")
                        : "omitted",
                }));
                console.table(preview);
                console.log("Full payload ↓");
                console.log(JSON.stringify(payloadArray, null, 2));
                console.groupEnd();
            } catch (e) {
                console.warn("[ProcessChangesModal] payload log failed:", e);
            }
        }

        try {
            const { data, status } = await axios.post(
                `${process.env.REACT_APP_API_URL}/facility/linkToFacility`,
                payloadArray,
                {
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                    },
                }
            );

            if (DEBUG_PAYLOADS) {
                console.groupCollapsed("[ProcessChangesModal] linkToFacility response");
                console.log("HTTP status:", status);
                console.log("Body:", data);
                console.groupEnd();
            }

            if (status === 200) {
                setOk(successMsg || "Processed.");
                await fetchData();
                return;
            }

            // non-200 success → show modal
            setLinkRespMode("accept");
            setLinkResp(data);
            setShowLinkRespModal(true);
            setOk(successMsg || "Sent.");
        } catch (e) {
            const resp = e?.response;
            if (DEBUG_PAYLOADS) {
                console.groupCollapsed("[ProcessChangesModal] linkToFacility error");
                console.log(resp?.status, resp?.data || e.message);
                console.groupEnd();
            }
            if (resp && resp.status === 409 && resp.data) {
                setLinkRespMode("accept");
                setLinkResp(resp.data);
                setShowLinkRespModal(true);
                return;
            }
            setErr(
                e?.response?.data?.error ||
                e?.message ||
                "Failed to send to /facility/linkToFacility."
            );
        }
    };

    // ---------- Resolver utils / handlers ----------
    const toItemsArray = (data) => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data?.rows)) return data.rows;
        return data ? [data] : [];
    };

    const hasConflicts = (body) => {
        const items = toItemsArray(body);
        return items.some((row) => {
            const lc = row?.link_conflict || row?.conflicts?.link_conflict;
            const dc = row?.date_conflict || row?.conflicts?.date_conflict;
            return (
                (lc && lc.status_of_conflict !== "accepted") ||
                (dc && dc.status_of_conflict !== "accepted")
            );
        });
    };

    const handleLinkModalResubmit = async (updatedPayload) => {
        setErr("");
        setOk("");

        const payloadArray = toItemsArray(updatedPayload);

        if (DEBUG_PAYLOADS) {
            console.groupCollapsed("[ProcessChangesModal] handleLinkModalResubmit CALLED");
            console.log("Rows:", payloadArray.length);
            console.log(JSON.stringify(payloadArray, null, 2));
            console.groupEnd();
        }

        try {
            const { data, status } = await axios.post(
                `${process.env.REACT_APP_API_URL}/facility/linkToFacility`,
                payloadArray,
                {
                    validateStatus: () => true,
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                    },
                }
            );

            if (DEBUG_PAYLOADS) {
                console.groupCollapsed("[ProcessChangesModal] handleLinkModalResubmit RESPONSE");
                console.log("HTTP status:", status);
                console.dir(data, { depth: null });
                console.groupEnd();
            }

            if (status >= 200 && status < 300 && !hasConflicts(data)) {
                setShowLinkRespModal(false);
                setLinkResp(null);
                setLinkRespMode("accept");
                setOk("Processed.");
                await fetchData();
                return;
            }

            // still conflicts or 409 → keep modal open
            const bodyForModal = data?.items || data?.rows || data;
            setLinkResp(bodyForModal);
            setShowLinkRespModal(true);
        } catch (e) {
            const resp = e?.response;
            if (resp?.status === 409 && resp?.data) {
                const bodyForModal = resp.data?.items || resp.data?.rows || resp.data;
                setLinkResp(bodyForModal);
                setShowLinkRespModal(true);
                return;
            }
            setErr(e?.response?.data?.error || e?.message || "Failed to resubmit.");
        }
    };

    const selectedParents = useMemo(() => buildSelectedObjects(), [apiData, selected]);
    const canDenySimple = useMemo(() => {
        if (!selectedParents.length) return false;
        return selectedParents.every((r) => Array.isArray(r.changes) && r.changes.length > 0);
    }, [selectedParents]);

    // ---------- Filters / paging ----------
    const handleStatusChange = async (e) => {
        const val = sanitizeStatus(e.target.value);
        setStatus(val);
        setPage(1);
        await fetchData({ status: val, page: 1 });
    };
    const handleShowAllToggle = async (e) => {
        const val = e.target.checked;
        setShowAll(val);
        setPage(1);
        await fetchData({ showAll: val, page: 1 });
    };
    const handleFacilityInput = (e) => {
        const val = e.target.value;
        setFacilityQuery(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(1);
            fetchData({ facility_name: val, page: 1 });
        }, 600);
    };
    const handlePageSizeChange = async (e) => {
        const newSize = Number(e.target.value) || 50;
        setPageSize(newSize);
        setPage(1);
        await fetchData({ page: 1, pageSize: newSize });
    };
    const goPrevPage = async () => {
        if (page <= 1) return;
        const p = page - 1;
        setPage(p);
        await fetchData({ page: p });
    };
    const goNextPage = async () => {
        if (page >= pageCount) return;
        const p = page + 1;
        setPage(p);
        await fetchData({ page: p });
    };

    const COLS = { ...COLS_BASE, facility: facilityColPx };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                scroll="paper"
                PaperProps={{ sx: { display: "flex", flexDirection: "column", height: "90vh" } }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Process Changes
                    <IconButton aria-label="close" onClick={onClose} sx={{ ml: "auto" }} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ flex: 1, p: 0, overflow: "hidden" }}>
                    {loading && <LinearProgress />}
                    {err && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {err}
                        </Alert>
                    )}
                    {ok && (
                        <Alert severity="success" sx={{ m: 2 }}>
                            {ok}
                        </Alert>
                    )}

                    <TableContainer sx={{ height: "100%", overflow: "auto" }}>
                        <div style={{ height: "100%", display: "grid", gridTemplateRows: "auto 1fr" }}>
                            {/* HEADER TABLE */}
                            <Table
                                size="small"
                                aria-label="changes header"
                                sx={{
                                    width: "100%",
                                    tableLayout: "fixed",
                                    "& th": { bgcolor: "background.paper" },
                                }}
                            >
                                <colgroup>
                                    <col style={{ width: COLS.sel }} />
                                    <col style={{ width: COLS.facility }} />
                                    <col style={{ width: COLS.service }} />
                                    <col style={{ width: COLS.start }} />
                                    <col style={{ width: COLS.stop }} />
                                    <col style={{ width: COLS.onHold }} />
                                    <col style={{ width: COLS.newStart }} />
                                    <col style={{ width: COLS.newStop }} />
                                    <col style={{ width: COLS.changeDate }} />
                                    <col style={{ width: COLS.status }} />
                                </colgroup>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            colSpan={10}
                                            sx={{ p: 0, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
                                        >
                                            <Stack
                                                ref={controlsRef}
                                                direction={{ xs: "column", sm: "row" }}
                                                alignItems={{ xs: "stretch", sm: "center" }}
                                                justifyContent="space-between"
                                                sx={{ px: 2, py: 1, gap: 2 }}
                                            >
                                                <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={2}>
                                                    <TextField
                                                        size="small"
                                                        label="Search facility"
                                                        placeholder="Type a facility name…"
                                                        value={facilityQuery}
                                                        onChange={handleFacilityInput}
                                                        sx={{ minWidth: 240 }}
                                                    />
                                                    <FormControl size="small" sx={{ minWidth: 180 }}>
                                                        <InputLabel id="status-filter-label">Status</InputLabel>
                                                        <Select
                                                            labelId="status-filter-label"
                                                            label="Status"
                                                            value={status}
                                                            onChange={handleStatusChange}
                                                        >
                                                            <MenuItem value="all">All</MenuItem>
                                                            <MenuItem value="pending">Pending</MenuItem>
                                                            <MenuItem value="denied">Denied</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                    <FormControlLabel
                                                        control={<Switch checked={showAll} onChange={handleShowAllToggle} color="primary" />}
                                                        label="Show all facilities"
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Selected: {selectedCount}/{selectableKeys.length}
                                                    </Typography>
                                                </Stack>

                                                <Stack direction="row" spacing={1}>
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        disabled={selectedParents.length === 0}
                                                        onClick={async () => {
                                                            const payload = buildPayloadAccept(selectedParents);
                                                            await sendToLinkToFacility(payload, `Sent ${payload.length} record(s).`);
                                                        }}
                                                    >
                                                        ACCEPT
                                                    </Button>

                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        disabled={!canDenySimple}
                                                        onClick={async () => {
                                                            const payload = buildPayloadDenySimple(selectedParents);
                                                            await sendToLinkToFacility(payload, `Denied ${payload.length} record(s).`);
                                                        }}
                                                    >
                                                        DENY
                                                    </Button>

                                                    {/* NEW: DENY + DATES opens resolver with ensured changes[] */}
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        disabled={selectedParents.length === 0}
                                                        onClick={() => {
                                                            const modalItems = buildModalPayloadForDenyWithDates(selectedParents);

                                                            if (DEBUG_PAYLOADS) {
                                                                console.groupCollapsed("[ProcessChangesModal] DENY + DATES → open resolver");
                                                                console.log(JSON.stringify(modalItems, null, 2));
                                                                console.groupEnd();
                                                            }

                                                            // Wrap in familiar shape for the resolver and open it.
                                                            setLinkRespMode("deny");
                                                            setLinkResp({
                                                                message:
                                                                    "Deny with dates – enter the new top-level start/hold/end dates, then Resubmit.",
                                                                nextAction: "await user corrections",
                                                                items: modalItems,
                                                            });
                                                            setShowLinkRespModal(true);
                                                        }}
                                                    >
                                                        DENY + DATES
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={
                                                    selectableKeys.length > 0 &&
                                                    selectedCount > 0 &&
                                                    selectedCount < selectableKeys.length
                                                }
                                                checked={
                                                    selectableKeys.length > 0 && selectedCount === selectableKeys.length
                                                }
                                                onChange={toggleAll}
                                            />
                                        </TableCell>
                                        <TableCell>Facility</TableCell>
                                        <TableCell>Service</TableCell>
                                        <TableCell align="center">Start</TableCell>
                                        <TableCell align="center">Stop</TableCell>
                                        <TableCell align="center">On Hold</TableCell>
                                        <TableCell align="center">New Start</TableCell>
                                        <TableCell align="center">New Stop</TableCell>
                                        <TableCell align="center">Change Date</TableCell>
                                        <TableCell align="center" sx={{ textTransform: "capitalize" }}>
                                            Status
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                            </Table>

                            {/* BODY TABLE */}
                            <div style={{ overflow: "auto" }}>
                                <Table size="small" aria-label="changes body" sx={{ width: "100%", tableLayout: "fixed" }}>
                                    <colgroup>
                                        <col style={{ width: COLS.sel }} />
                                        <col style={{ width: COLS.facility }} />
                                        <col style={{ width: COLS.service }} />
                                        <col style={{ width: COLS.start }} />
                                        <col style={{ width: COLS.stop }} />
                                        <col style={{ width: COLS.onHold }} />
                                        <col style={{ width: COLS.newStart }} />
                                        <col style={{ width: COLS.newStop }} />
                                        <col style={{ width: COLS.changeDate }} />
                                        <col style={{ width: COLS.status }} />
                                    </colgroup>

                                    <TableBody>
                                        {flatRows.map((row) => {
                                            const isChecked = selected.has(row.key);
                                            return (
                                                <TableRow
                                                    key={row.key}
                                                    hover
                                                    sx={{
                                                        backgroundColor: row.linked_to ? "#E8F5E9" : "#FFFFFF",
                                                        "&:hover": {
                                                            backgroundColor: row.linked_to ? "#DFF3E3" : "action.hover",
                                                        },
                                                    }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={isChecked}
                                                            onChange={() => row.selectable && toggleOne(row.key)}
                                                            disabled={!row.selectable}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title={`linked_to: ${row.linked_to || "—"}`}>
                              <span
                                  style={{
                                      display: "block",
                                      width: "100%",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                  }}
                              >
                                {(row.facility_name || "").length > nameLimit
                                    ? `${row.facility_name.slice(0, nameLimit - 1)}…`
                                    : row.facility_name || ""}
                              </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>{row.service_name}</TableCell>
                                                    <TableCell align="center">{fmt(row.start_date)}</TableCell>
                                                    <TableCell align="center">{fmt(row.end_date)}</TableCell>
                                                    <TableCell align="center">{fmt(row.on_hold_date)}</TableCell>
                                                    <TableCell align="center">{fmt(row.new_start_date)}</TableCell>
                                                    <TableCell align="center">{fmt(row.new_end_date)}</TableCell>
                                                    <TableCell align="center">{fmt(row.change_date)}</TableCell>
                                                    <TableCell align="center" sx={{ textTransform: "capitalize" }}>
                                                        {statusLabel(row.accepted)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}

                                        {!flatRows.length && (
                                            <TableRow>
                                                <TableCell colSpan={10}>
                                                    <Typography align="center" color="text.secondary">
                                                        No results for current filters.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    </TableContainer>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                        borderTop: (t) => `1px solid ${t.palette.divider}`,
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                        <IconButton onClick={goPrevPage} disabled={page <= 1} size="small">
                            <NavigateBeforeIcon />
                        </IconButton>
                        <IconButton onClick={goNextPage} disabled={page >= pageCount} size="small">
                            <NavigateNextIcon />
                        </IconButton>

                        <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                            Page {page} of {pageCount}
                        </Typography>

                        <FormControl size="small" sx={{ minWidth: 96 }}>
                            <InputLabel id="rows-per-page-label">Rows</InputLabel>
                            <Select
                                labelId="rows-per-page-label"
                                label="Rows"
                                value={pageSize}
                                onChange={handlePageSizeChange}
                            >
                                {PAGE_SIZE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="body2" color="text.secondary">
                            Total: {total.toLocaleString()}
                        </Typography>
                    </Stack>

                    <Button onClick={onClose} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resolver modal */}
            <LinkToFacilityResponseModal
                open={showLinkRespModal}
                onClose={() => setShowLinkRespModal(false)}
                response={linkResp}
                mode={linkRespMode}
                onResubmit={handleLinkModalResubmit}
            />
        </>
    );
}
